import axios from "axios";
import { getLang } from "./i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Reads the JS-visible CSRF cookie (double-submit token) set on login/refresh.
function csrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)amd_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

// Auto-stamp the CSRF token on every mutating request so no admin call can forget it.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method !== "get" && method !== "head") {
    config.headers = config.headers || {};
    if (!config.headers["X-CSRF-Token"]) config.headers["X-CSRF-Token"] = csrfToken();
  }
  return config;
});

// --- Admin auth (httpOnly cookies; auto-refresh access token on 401) ---
let _refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthCall = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && !original._retried && !isAuthCall) {
      original._retried = true;
      try {
        _refreshing = _refreshing || api.post("/auth/refresh");
        await _refreshing;
        _refreshing = null;
        if (original.headers && original.headers["X-CSRF-Token"] !== undefined) {
          original.headers["X-CSRF-Token"] = csrfToken();
        }
        return api(original);
      } catch (e) {
        _refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export const adminLogin = (email, password) =>
  api
    .post("/auth/login", JSON.stringify({ email, password }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const adminMe = () => api.get("/auth/me").then((r) => r.data);
export const adminLogout = () => api.post("/auth/logout").then((r) => r.data).catch(() => {});
export const getAdminAudit = (params = {}) =>
  api.get("/admin/audit", { params }).then((r) => r.data.entries);
export const exportAuditCsv = (params = {}) =>
  api.get("/admin/audit", { params: { ...params, format: "csv" }, responseType: "blob" }).then((r) => r.data);
export const clearAuditLog = () =>
  api
    .post("/admin/audit/clear", "", {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const listPhysicians = () => api.get("/admin/physicians").then((r) => r.data.physicians);
export const addPhysician = (name, email, tiers) =>
  api
    .post("/admin/physicians", JSON.stringify({ name, email, tiers }), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const togglePhysician = (physician_id, active) =>
  api
    .post("/admin/physicians/toggle", JSON.stringify({ physician_id, active }), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const reassignRequest = (request_id, physician_id) =>
  api
    .post("/admin/reassign", JSON.stringify({ request_id, physician_id }), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

// Doctor response (public, token-gated — no admin cookie)
export const getDoctorRequest = (requestId, token) =>
  api.get(`/doctor/request/${requestId}`, { params: { token } }).then((r) => r.data);
export const submitDoctorResponse = (requestId, token, note, attachments) =>
  api
    .post(`/doctor/request/${requestId}/respond`, JSON.stringify({ note, attachments }), {
      params: { token },
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const askQuestion = (question, history = []) =>
  api
    .post("/ask", JSON.stringify({ question, history, lang: getLang() }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const sendFeedback = (answer_id, helpful, comments) =>
  api
    .post("/feedback", JSON.stringify({ answer_id, helpful, comments }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const getCategories = () => api.get("/categories").then((r) => r.data.categories);

export const getLearnIndex = (lang) => api.get(`/learn?lang=${lang || getLang()}`).then((r) => r.data.pages);

export const getLearnPage = (slug, lang) =>
  api.get(`/learn/${slug}?lang=${lang || getLang()}`).then((r) => r.data);


export const getDashboard = () => api.get("/admin/dashboard").then((r) => r.data);
export const getAdminQuestions = (emergency = false, limit = 50, days = 0, category = "", translate = false) =>
  api.get("/admin/questions", { params: { emergency, limit, days, category, translate } }).then((r) => r.data);
export const exportQuestionsCsv = (emergency = false, limit = 200, days = 0, category = "", translate = false) =>
  api.get("/admin/questions", { params: { emergency, limit, days, category, translate, format: "csv" }, responseType: "blob" }).then((r) => r.data);

export const getAdminSettings = () => api.get("/admin/settings").then((r) => r.data);

export const saveAdminSettings = (payload) =>
  api
    .post("/admin/settings", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const sendTestDigest = () =>
  api
    .post("/admin/digest-test", "", {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const getPopular = (lang) => api.get(`/popular?lang=${lang || getLang()}`).then((r) => r.data.questions);

export const updateDoctorStatus = (request_id, status, note, attachments) =>
  api
    .post("/doctor-request/status", JSON.stringify({ request_id, status, note, attachments }), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const refundDoctorRequest = (request_id, reason, notify = true) =>
  api
    .post("/admin/refund", JSON.stringify({ request_id, reason, notify }), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const replyDoctorRequest = (request_id, message, attachments) =>
  api
    .post(
      `/doctor-request/${request_id}/reply`,
      JSON.stringify({ message, attachments: attachments || [] }),
      { headers: { "Content-Type": "text/plain" }, transformRequest: [(d) => d] }
    )
    .then((r) => r.data);

export const uploadPhoto = (file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/upload/photo", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

export const fileUrl = (path, sig) =>
  `${API}/files/${path}${sig ? `?sig=${encodeURIComponent(sig)}` : ""}`;

export const addLibraryTopic = (question) =>
  api
    .post("/admin/library-topic", JSON.stringify({ question }), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const simplifyAnswer = (text, lang) =>
  api
    .post("/simplify", JSON.stringify({ text, lang: lang || getLang() }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data.text);

export const createDoctorRequest = (payload) =>
  api
    .post("/doctor-request", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const createShare = (payload) =>
  api
    .post("/share", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const getShare = (id) => api.get(`/share/${id}`).then((r) => r.data);

export const emailSummary = (payload) =>
  api
    .post("/email/summary", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const getSubscribers = () => api.get("/admin/subscribers").then((r) => r.data);
export const exportSubscribersCsv = () =>
  api.get("/admin/subscribers", { params: { format: "csv" }, responseType: "blob" }).then((r) => r.data);
export const sendBroadcast = (payload) =>
  api
    .post("/admin/broadcast", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain", "X-CSRF-Token": csrfToken() },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const getBroadcasts = () => api.get("/admin/broadcasts").then((r) => r.data);
export const cancelBroadcast = (bid) =>
  api
    .post("/admin/broadcast/cancel", JSON.stringify({ bid }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const draftBroadcast = () =>
  api.post("/admin/broadcast/draft", "", { transformRequest: [(d) => d] }).then((r) => r.data);
export const resendConfirmation = (email) =>
  api
    .post("/admin/subscribers/resend-confirm", JSON.stringify({ email }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const updateScheduled = (payload) =>
  api
    .post("/admin/broadcast/update", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const getRecurring = () => api.get("/admin/recurring").then((r) => r.data);
export const testSend = (payload) =>
  api
    .post("/admin/broadcast/test", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const getOpenHours = () => api.get("/admin/open-hours").then((r) => r.data);
export const getQuietHours = () => api.get("/admin/quiet-hours").then((r) => r.data);
export const saveQuietHours = (payload) =>
  api
    .post("/admin/quiet-hours", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const runAbDemo = (payload) =>
  api
    .post("/admin/broadcast/ab-demo", JSON.stringify(payload || {}), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const getWeeklyDigest = () => api.get("/admin/weekly-digest").then((r) => r.data);
export const setWeeklyDigest = (active) =>
  api
    .post("/admin/weekly-digest", JSON.stringify({ active }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);
export const sendWeeklyDigestNow = () =>
  api
    .post("/admin/weekly-digest/send", "", { transformRequest: [(d) => d] })
    .then((r) => r.data);
export const previewWeeklyDigest = () => api.get("/admin/weekly-digest/preview").then((r) => r.data);
export const saveRecurring = (payload) =>
  api
    .post("/admin/recurring", JSON.stringify(payload), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const registerCertificate = (cert_id, name) =>
  api
    .post("/certificate", JSON.stringify({ cert_id, name: name || "" }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data)
    .catch(() => {});

export const verifyCertificate = (cert_id) =>
  api.get(`/certificate/${cert_id}`).then((r) => r.data);

export const createReviewCheckout = (payload) =>
  api
    .post("/payments/checkout", JSON.stringify({ ...payload, lang: getLang() }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .then((r) => r.data);

export const getPaymentStatus = (sessionId) =>
  api.get(`/payments/status/${sessionId}`).then((r) => r.data);

export const recordUpsellClick = (variant) =>
  api.post("/events/upsell", JSON.stringify({ variant }), {
    headers: { "Content-Type": "text/plain" },
    transformRequest: [(d) => d],
  }).then((r) => r.data).catch(() => {});

export const getUpsellConfig = () =>
  api.get("/upsell/config").then((r) => r.data).catch(() => ({ locked: false, variant: null }));

export const getDoctorRequestStatus = (request_id) =>
  api.get(`/doctor-request/${request_id}/status`).then((r) => r.data);

// Streams the answer word-by-word via SSE. Calls handler callbacks as events arrive.
export async function askQuestionStream(question, history, { onMeta, onDelta, onDone, onError, signal }) {
  try {
    const res = await fetch(`${API}/ask/stream`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: JSON.stringify({ question, history, lang: getLang() }),
      signal,
    });
    if (!res.ok || !res.body) throw new Error("stream failed");

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (signal && signal.aborted) return;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop();
      for (const chunk of chunks) {
        const line = chunk.trim();
        if (!line.startsWith("data:")) continue;
        let evt;
        try {
          evt = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }
        if (evt.type === "meta") onMeta && onMeta(evt);
        else if (evt.type === "delta") onDelta && onDelta(evt.content);
        else if (evt.type === "done") onDone && onDone(evt);
        else if (evt.type === "error") onError && onError(evt.message);
      }
    }
  } catch (e) {
    if (e && e.name === "AbortError") return;
    onError && onError("Sorry, something went wrong. Please try again.");
  }
}


// Fire once per browser session to record a site visit (best-effort).
function visitorId() {
  try {
    let v = localStorage.getItem("amd_vid");
    if (!v) {
      v = (crypto?.randomUUID?.() || String(Date.now()) + Math.random().toString(36).slice(2));
      localStorage.setItem("amd_vid", v);
    }
    return v;
  } catch {
    return null;
  }
}

export function trackVisit() {
  try {
    if (sessionStorage.getItem("amd_visit_tracked")) return;
    sessionStorage.setItem("amd_visit_tracked", "1");
  } catch {
    // sessionStorage unavailable — still record the visit
  }
  api
    .post("/track/visit", JSON.stringify({ visitor_id: visitorId() }), {
      headers: { "Content-Type": "text/plain" },
      transformRequest: [(d) => d],
    })
    .catch(() => {});
}
